
import { useState, useRef, useEffect } from "react";
import ReactCrop, { type Crop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";

interface ImageCropperProps {
  image: string;
  onCrop: (croppedImage: Blob) => void;
  isOpen: boolean;
  onClose: () => void;
}

export function ImageCropper({ image, onCrop, isOpen, onClose }: ImageCropperProps) {
  const [crop, setCrop] = useState<Crop | null>(null);
  const imageRef = useRef<HTMLImageElement>(null);

  // Reset crop when dialog opens
  useEffect(() => {
    if (isOpen) {
      setCrop(null);
    }
  }, [isOpen]);

  const getCroppedImg = async () => {
    if (!imageRef.current || !crop) return;

    const canvas = document.createElement('canvas');
    const scaleX = imageRef.current.naturalWidth / imageRef.current.width;
    const scaleY = imageRef.current.naturalHeight / imageRef.current.height;

    canvas.width = 400;
    canvas.height = 400;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const sourceX = crop.x * scaleX;
    const sourceY = crop.y * scaleY;
    const sourceWidth = crop.width * scaleX;
    const sourceHeight = crop.height * scaleY;

    ctx.drawImage(
      imageRef.current,
      sourceX,
      sourceY,
      sourceWidth,
      sourceHeight,
      0,
      0,
      canvas.width,
      canvas.height
    );

    return new Promise<Blob>((resolve) => {
      canvas.toBlob((blob) => {
        if (blob) {
          resolve(blob);
        }
      }, 'image/jpeg', 0.95);
    });
  };

  const handleSave = async () => {
    const croppedImage = await getCroppedImg();
    if (croppedImage) {
      onCrop(croppedImage);
    }
  };

  const onCropChange = (newCrop: Crop) => {
    if (newCrop.width !== newCrop.height) {
      const size = Math.min(newCrop.width, newCrop.height);
      setCrop({
        ...newCrop,
        unit: 'px',
        width: size,
        height: size
      });
    } else {
      setCrop(newCrop);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-[90vw] md:max-w-[800px] p-6">
        <DialogHeader>
          <DialogTitle>Crop Profile Picture</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-4">
          <div className="w-full flex items-center justify-center bg-muted/50 rounded-lg">
            <div className="relative w-full flex items-center justify-center p-4">
              <ReactCrop
                crop={crop}
                onChange={onCropChange}
                aspect={1}
                keepSelection
                className="max-w-full max-h-[70vh]"
                style={{ maxWidth: '100%' }}
              >
                <img
                  ref={imageRef}
                  src={image}
                  alt="Crop"
                  className="max-w-full max-h-[70vh] w-auto h-auto object-contain"
                  onLoad={(e) => {
                    // Only set crop if it's not already set
                    if (!crop) {
                      const target = e.currentTarget;
                      // Use a slightly larger initial size (35% of the smaller dimension instead of 30%)
                      const size = Math.min(target.width, target.height) * 0.35;
                      
                      // Ensure the crop is centered
                      const x = (target.width - size) / 2;
                      const y = (target.height - size) / 2;
                      
                      // Set the crop with proper positioning
                      setCrop({
                        unit: 'px',
                        width: size,
                        height: size,
                        x: x,
                        y: y,
                      });
                    }
                  }}
                />
              </ReactCrop>
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button onClick={handleSave}>Save</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
